import React, {
    useContext, useEffect, useState, useMemo
} from 'react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import SgFormFieldCtrl from 'sg-form-field-ctrl';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { SgVehicleValidator, SgProductService } from 'saga-modules';
import messages from './VehicleUse.message';
import { CarUseRadiosMetadata, CarUseDropDownMetadata, MotorhomeUseMetadata } from './metadata/index';

function SgVehicleUse(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        onValidate: onValidateParent,
        path,
        isVehicleAssignmentSection
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const [metadata, setMetadata] = useState(SgProductService.isMotorHome() ? MotorhomeUseMetadata : CarUseRadiosMetadata);
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isVehicleUseValid = SgVehicleValidator.isVehicleUseValid({ modelVM: modelVM });
    const { vehicleUse } = _.get(modelVM, path);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const getSDPHelpText = () => {
        return (
            <div>
                <b>{translator(messages.sdpLabel)}</b>
                {translator(messages.sdpInfo)}
            </div>
        );
    };

    const getCommutingHelpText = () => {
        return (
            <div>
                <b>{translator(messages.commutingLabel)}</b>
                {translator(messages.commutingInfo)}
            </div>
        );
    };

    const getPersonalBusinessHelpText = () => {
        return (
            <div>
                <b>{translator(messages.personalBusinessLabel)}</b>
                {translator(messages.personalBusinessInfo)}
            </div>
        );
    };

    const getBusinessHelpText = () => {
        return (
            <div>
                <b>{translator(messages.businessLabel)}</b>
                {translator(messages.businessInfo)}
            </div>
        );
    };

    const isVehicleUseCode = (expectedCode) => {
        const vehicleUseValue = vehicleUse.value;
        if (!_.isNil(vehicleUseValue)) {
            return vehicleUseValue.code === expectedCode;
        }
        return expectedCode === 'sdp_only';
    };

    const arrowPositionClass = useMemo(() => {
        const vehicleUseValue = vehicleUse.value;
        if (_.isNil(vehicleUseValue)) {
            return 'position1';
        }
        switch (vehicleUseValue.code) {
            case 'sdp_only':
                return 'position1';
            case 'sdp_commuting':
                return 'position2';
            case 'sdp_business_use_ph_and_partner':
                return 'position3';
            case 'sdp_business_use_all_drivers':
                return 'position4';
            default:
                return 'position1';
        }
    }, [vehicleUse.value]);

    const [vehicleUseHelpText, setVehicleUseHelpText] = useState(translator(messages.vehicleUseHelpText));

    useEffect(() => {
        if (!SgProductService.isMotorHome()) {
            if (breakpoint === 'phone' || isVehicleAssignmentSection) {
                setMetadata(CarUseDropDownMetadata);
                setVehicleUseHelpText(translator(messages.vehicleUseMobileSecondaryLabel));
            } else {
                setMetadata(CarUseRadiosMetadata);
                setVehicleUseHelpText(translator(messages.vehicleUseHelpText));
            }
        }
    }, [breakpoint, translator]);

    const vehiclesUseValidationMessage = useMemo(() => {
        if (isVehicleAssignmentSection && !isVehicleUseValid) {
            const selectedUse = translator({ id: vehicleUse.value.name, defaultMessage: '' });
            return [translator(messages.vehicleUseEmploymentValidation, { vehicleUse: `'${selectedUse}'` })];
        }
        if (vehicleUse.value === undefined) {
            return vehicleUse.aspects.validationMessages;
        }
        return null;
    }, [isVehicleAssignmentSection, isVehicleUseValid, vehicleUse.value, vehicleUse.aspects.validationMessages, translator]);

    const overrideProps = {
        vehicleUse: {
            label: translator(messages.vehicleUseLabel),
            secondaryLabel: !isVehicleAssignmentSection ? vehicleUseHelpText : null,
            validationMessages: vehiclesUseValidationMessage,
            showErrors: isVehicleAssignmentSection ? !isVehicleUseValid : showErrors
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
        },
        resolveComponentMap: {
            SgFormFieldCtrl
        }
    };

    return (
        <>
            <ViewModelForm
                uiProps={metadata.componentContent}
                model={modelVM}
                showErrors={showErrors}
                onModelChange={updateData}
                onValidationChange={onValidate}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
            />
            {!SgProductService.isMotorHome() && !isVehicleAssignmentSection
                ? (
                    <div className={`helpTextSlider ${arrowPositionClass}`}>
                        {isVehicleUseCode('sdp_only') ? getSDPHelpText() : null}
                        {isVehicleUseCode('sdp_commuting') ? getCommutingHelpText() : null}
                        {isVehicleUseCode('sdp_business_use_ph_and_partner') ? getPersonalBusinessHelpText() : null}
                        {isVehicleUseCode('sdp_business_use_all_drivers') ? getBusinessHelpText() : null}
                    </div>
                )
                : null}

        </>
    );
}

export default SgVehicleUse;
